import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Input,
  Breadcrumb,
  Button,
  message,
  Modal,
  DatePicker,
  Select,
  Space,
  Grid,
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Avatar,
  Tooltip,
  Dropdown,
  Tag,
  Descriptions,
} from 'antd';
import { Link } from 'react-router-dom';
import {
  RightOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  SearchOutlined,
  CalendarOutlined,
  UserOutlined,
  HistoryOutlined,
  EyeOutlined,
  LoginOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  FileTextOutlined,
  TeamOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { auditService } from '../../common/services';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const getAvatarColor = (name) => {
  const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#87d068', '#1890ff', '#722ed1', '#eb2f96'];
  const index = (name || '').charCodeAt(0) % colors.length;
  return colors[index];
};

const ACTION_CONFIG = {
  login:      { label: 'Connexion',    color: 'blue',    icon: <LoginOutlined /> },
  page_visit: { label: 'Visite page',  color: 'cyan',    icon: <EyeOutlined /> },
  create:     { label: 'Création',     color: 'green',   icon: <PlusOutlined /> },
  update:     { label: 'Modification', color: 'orange',  icon: <EditOutlined /> },
  delete:     { label: 'Suppression',  color: 'red',     icon: <DeleteOutlined /> },
  other:      { label: 'Autre',        color: 'default', icon: <FileTextOutlined /> },
};

const AuditLog = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, uniqueAdmins: 0, today: 0, thisMonth: 0 });
  const [adminsList, setAdminsList] = useState([]);

  const [q, setQ] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [actionFilter, setActionFilter] = useState(undefined);
  const [adminFilter, setAdminFilter] = useState(undefined);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sort, setSort] = useState('-createdAt');

  const [detailModal, setDetailModal] = useState({ open: false, record: null });

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;

  // Charger la liste des admins pour le filtre
  useEffect(() => {
    auditService.getAdmins()
      .then((data) => setAdminsList(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Charger les stats
  const fetchStats = useCallback(async () => {
    try {
      const data = await auditService.getStats();
      if (data) {
        setStats({
          total: data.total || 0,
          uniqueAdmins: data.uniqueAdmins || 0,
          today: data.today || 0,
          thisMonth: data.thisMonth || 0,
        });
      }
    } catch (_) {}
  }, []);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page, limit, sort,
        q: q || undefined,
        action: actionFilter || undefined,
        adminId: adminFilter || undefined,
        start: dateRange?.[0]?.format('YYYY-MM-DD'),
        end: dateRange?.[1]?.format('YYYY-MM-DD'),
      };
      const data = await auditService.getList(params);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('Erreur chargement audit logs:', e);
      message.error("Erreur lors du chargement de l'historique");
    } finally {
      setLoading(false);
    }
  }, [page, limit, sort, q, dateRange, actionFilter, adminFilter]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Export
  const fetchAllForExport = async () => {
    const params = {
      page: 1, limit: 5000, sort,
      q: q || undefined,
      action: actionFilter || undefined,
      adminId: adminFilter || undefined,
      start: dateRange?.[0]?.format('YYYY-MM-DD'),
      end: dateRange?.[1]?.format('YYYY-MM-DD'),
    };
    const data = await auditService.getList(params);
    return data.items || [];
  };

  const formatRow = (it) => ({
    Admin: it.adminName || '',
    Téléphone: it.adminPhone || '',
    Rôle: it.adminRole || '',
    Action: ACTION_CONFIG[it.action]?.label || it.action,
    Ressource: it.resource || '',
    Description: it.description || '',
    Route: it.routePath || '',
    Statut: it.statusCode || '',
    Date: it.createdAt ? dayjs(it.createdAt).format('DD/MM/YYYY HH:mm') : '',
  });

  const exportExcel = async () => {
    try {
      message.loading({ content: 'Export en cours...', key: 'export' });
      const all = await fetchAllForExport();
      const rows = all.map(formatRow);
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Audit_Log');
      XLSX.writeFile(wb, 'audit_log.xlsx');
      message.success({ content: 'Export Excel réussi!', key: 'export' });
    } catch (e) {
      message.error({ content: 'Export Excel: une erreur est survenue.', key: 'export' });
    }
  };

  const exportCSV = async () => {
    try {
      message.loading({ content: 'Export en cours...', key: 'export' });
      const all = await fetchAllForExport();
      const rows = all.map(formatRow);
      const ws = XLSX.utils.json_to_sheet(rows);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'audit_log.csv');
      message.success({ content: 'Export CSV réussi!', key: 'export' });
    } catch (e) {
      message.error({ content: 'Export CSV: une erreur est survenue.', key: 'export' });
    }
  };

  const exportPDF = async () => {
    try {
      message.loading({ content: 'Export en cours...', key: 'export' });
      const all = await fetchAllForExport();
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.text("Historique d'activité des administrateurs", 14, 10);
      autoTable(doc, {
        startY: 20,
        head: [['Admin', 'Action', 'Ressource', 'Description', 'Route', 'Statut', 'Date']],
        body: all.map(it => [
          it.adminName || '',
          ACTION_CONFIG[it.action]?.label || it.action,
          it.resource || '',
          it.description || '',
          it.routePath || '',
          it.statusCode || '',
          it.createdAt ? dayjs(it.createdAt).format('DD/MM/YYYY HH:mm') : '',
        ]),
        styles: { fontSize: 7 },
      });
      doc.save('audit_log.pdf');
      message.success({ content: 'Export PDF réussi!', key: 'export' });
    } catch (e) {
      message.error({ content: 'Export PDF: une erreur est survenue.', key: 'export' });
    }
  };

  const exportMenuItems = [
    { key: 'excel', icon: <FileExcelOutlined style={{ color: '#52c41a' }} />, label: 'Excel (.xlsx)', onClick: exportExcel },
    { key: 'csv', icon: <DownloadOutlined style={{ color: '#1890ff' }} />, label: 'CSV', onClick: exportCSV },
    { key: 'pdf', icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />, label: 'PDF', onClick: exportPDF },
  ];

  const showDetail = (record) => {
    setDetailModal({ open: true, record });
  };

  const columns = [
    {
      title: 'Administrateur',
      key: 'admin',
      width: 180,
      render: (_, record) => {
        const name = record.adminName || 'Admin';
        const initials = name.split(' ').map(w => w.charAt(0)).join('').substring(0, 2).toUpperCase();
        return (
          <Space>
            <Avatar style={{ backgroundColor: getAvatarColor(name) }} size={36}>
              {initials}
            </Avatar>
            <div>
              <Text strong style={{ display: 'block', fontSize: 13 }}>{name}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{record.adminRole || ''}</Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 130,
      filters: Object.entries(ACTION_CONFIG).map(([key, cfg]) => ({ text: cfg.label, value: key })),
      render: (action) => {
        const cfg = ACTION_CONFIG[action] || ACTION_CONFIG.other;
        return (
          <Tag icon={cfg.icon} color={cfg.color}>
            {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: 'Ressource',
      dataIndex: 'resource',
      key: 'resource',
      width: 120,
      render: (v) => <Text style={{ fontSize: 13 }}>{v || '-'}</Text>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 250,
      render: (v) => (
        <Tooltip title={v}>
          <Text style={{ fontSize: 12 }}>{v || '-'}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'statusCode',
      key: 'statusCode',
      width: 80,
      render: (code) => {
        if (!code) return '-';
        const color = code >= 200 && code < 300 ? 'green' : code >= 400 ? 'red' : 'orange';
        return <Tag color={color}>{code}</Tag>;
      },
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      sorter: true,
      render: (d) => {
        if (!d) return '-';
        const formatted = dayjs(d).format('DD/MM/YYYY HH:mm');
        const parts = formatted.split(' ');
        return (
          <Tooltip title={formatted}>
            <Space direction="vertical" size={0}>
              <Text style={{ fontSize: 12 }}>{parts[0]}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{parts[1]}</Text>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: '',
      key: 'detail',
      width: 50,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="Détail">
          <Button
            type="text"
            icon={<InfoCircleOutlined />}
            onClick={() => showDetail(record)}
          />
        </Tooltip>
      ),
    },
  ];

  const onTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 20);

    if (sorter?.columnKey === 'createdAt') {
      setSort(sorter.order === 'ascend' ? 'createdAt' : '-createdAt');
    }

    if (filters?.action) {
      setActionFilter(filters.action.length === 1 ? filters.action[0] : undefined);
    }
  };

  return (
    <>
      {/* Breadcrumb */}
      <Breadcrumb
        separator={<RightOutlined />}
        items={[
          { title: <Link to="/dashboard">Accueil</Link> },
          { title: 'Administrateurs' },
          { title: "Historique d'activité" },
        ]}
        style={{ marginBottom: 16 }}
      />

      {/* Statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none'
            }}
          >
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Total activités</span>}
              value={stats.total}
              valueStyle={{ color: '#fff', fontSize: isMobile ? 20 : 24 }}
              prefix={<HistoryOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{ background: '#e6f7ff', borderColor: '#1890ff' }}
          >
            <Statistic
              title={<span style={{ color: '#1890ff' }}>Admins actifs</span>}
              value={stats.uniqueAdmins}
              valueStyle={{ color: '#1890ff', fontSize: isMobile ? 20 : 24 }}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{ background: '#f6ffed', borderColor: '#52c41a' }}
          >
            <Statistic
              title={<span style={{ color: '#52c41a' }}>Aujourd'hui</span>}
              value={stats.today}
              valueStyle={{ color: '#52c41a', fontSize: isMobile ? 20 : 24 }}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{ background: '#fff7e6', borderColor: '#fa8c16' }}
          >
            <Statistic
              title={<span style={{ color: '#fa8c16' }}>Ce mois</span>}
              value={stats.thisMonth}
              valueStyle={{ color: '#fa8c16', fontSize: isMobile ? 20 : 24 }}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtres et tableau */}
      <Card>
        {/* Filtres */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={6}>
            <Input
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Rechercher..."
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              allowClear
              size={isMobile ? 'middle' : 'large'}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Action"
              value={actionFilter}
              onChange={(v) => { setActionFilter(v); setPage(1); }}
              allowClear
              style={{ width: '100%' }}
              size={isMobile ? 'middle' : 'large'}
              options={Object.entries(ACTION_CONFIG).map(([key, cfg]) => ({
                value: key,
                label: cfg.label,
              }))}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Administrateur"
              value={adminFilter}
              onChange={(v) => { setAdminFilter(v); setPage(1); }}
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              size={isMobile ? 'middle' : 'large'}
              options={adminsList.map((a) => ({
                value: a._id,
                label: `${a.name} (${a.role})`,
              }))}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              onChange={(r) => { setDateRange(r); setPage(1); }}
              placeholder={['Date début', 'Date fin']}
              format="DD/MM/YYYY"
              allowClear
              size={isMobile ? 'middle' : 'large'}
              style={{ width: '100%' }}
              suffixIcon={<CalendarOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Dropdown
              menu={{ items: exportMenuItems }}
              trigger={['click']}
            >
              <Button
                icon={<DownloadOutlined />}
                size={isMobile ? 'middle' : 'large'}
                style={{ width: '100%' }}
              >
                Exporter <RightOutlined style={{ fontSize: 10, marginLeft: 4 }} />
              </Button>
            </Dropdown>
          </Col>
        </Row>

        {/* Résultats */}
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">
            {total} activité{total > 1 ? 's' : ''} trouvée{total > 1 ? 's' : ''}
          </Text>
        </div>

        {/* Tableau */}
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={items}
          loading={isLoading}
          onChange={onTableChange}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: 1000 }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total}`,
          }}
          rowClassName={(_, index) =>
            index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
          }
        />
      </Card>

      {/* Modal Détail */}
      <Modal
        title="Détail de l'activité"
        open={detailModal.open}
        onCancel={() => setDetailModal({ open: false, record: null })}
        footer={null}
        width={isMobile ? '95%' : 640}
      >
        {detailModal.record && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Administrateur">
              <Space>
                <UserOutlined />
                {detailModal.record.adminName}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Téléphone">{detailModal.record.adminPhone || '-'}</Descriptions.Item>
            <Descriptions.Item label="Rôle">{detailModal.record.adminRole || '-'}</Descriptions.Item>
            <Descriptions.Item label="Action">
              {(() => {
                const cfg = ACTION_CONFIG[detailModal.record.action] || ACTION_CONFIG.other;
                return <Tag icon={cfg.icon} color={cfg.color}>{cfg.label}</Tag>;
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="Ressource">{detailModal.record.resource || '-'}</Descriptions.Item>
            <Descriptions.Item label="ID Ressource">{detailModal.record.resourceId || '-'}</Descriptions.Item>
            <Descriptions.Item label="Description">{detailModal.record.description || '-'}</Descriptions.Item>
            <Descriptions.Item label="Méthode HTTP">
              {detailModal.record.httpMethod ? <Tag>{detailModal.record.httpMethod}</Tag> : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Route">{detailModal.record.routePath || '-'}</Descriptions.Item>
            <Descriptions.Item label="Code statut">
              {detailModal.record.statusCode ? (
                <Tag color={detailModal.record.statusCode < 300 ? 'green' : 'red'}>
                  {detailModal.record.statusCode}
                </Tag>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Adresse IP">{detailModal.record.ipAddress || '-'}</Descriptions.Item>
            <Descriptions.Item label="Date">
              {detailModal.record.createdAt
                ? dayjs(detailModal.record.createdAt).format('DD/MM/YYYY HH:mm:ss')
                : '-'}
            </Descriptions.Item>
            {detailModal.record.metadata && Object.keys(detailModal.record.metadata).length > 0 && (
              <Descriptions.Item label="Métadonnées">
                <pre style={{ margin: 0, fontSize: 11, maxHeight: 200, overflow: 'auto' }}>
                  {JSON.stringify(detailModal.record.metadata, null, 2)}
                </pre>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* Styles */}
      <style>{`
        .table-row-light {
          background-color: #ffffff;
        }
        .table-row-dark {
          background-color: #fafafa;
        }
        .table-row-light:hover td,
        .table-row-dark:hover td {
          background-color: #e6f7ff !important;
        }
      `}</style>
    </>
  );
};

export default AuditLog;
